import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import XChallengeCard from "../components/XChallengeCard";

type Standing = {
  name: string;
  points: number;
  codeCardsEarned: number;
  codeCardsGivenOut: number;
};

type WeeklyChallenge = {
  title: string;
  short?: string | null;
  weekstart: string;
  weekend: string;
  reward: string;
};

type CompletedChallenge = {
  title: string;
  description?: string | null;
  weekStart: string;
  weekEnd: string;
  reward: string;
};

type PlayerChallengeHistory = {
  playerName: string;
  challenges: CompletedChallenge[];
};

const API_BASE =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api";

const WEEKLY_URL = `${API_BASE}/weekly-challenges/get`;
const WEEKLY_LAST_URL = `${API_BASE}/weekly-challenges/get-last-weeks`;
const STANDINGS_URL = `${API_BASE}/standings/get`;
const HISTORY_URL = `${API_BASE}/player-challenges/history`;


const challengeNotices = [
  "November 5-én, csütörtökön 18:00–19:00 között dupla XP jár a fejlesztésekért a Spotlight Hour alatt. Érdemes erre az órára tartogatni a Machopokat, a boszorkánykalapos medvéket!",
  "Bár ezen a héten nincs telihold, egy kis különleges holdfénnyel Ursaluna így is elérhető…",
];


export default function HalfYear() {
  const { t, i18n } = useTranslation();

  const [weeklyChallenges, setWeeklyChallenges] = useState<WeeklyChallenge[]>([]);
  const [lastWeeklyChallenges, setLastWeeklyChallenges] = useState<WeeklyChallenge[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [challengeHistory, setChallengeHistory] =
    useState<PlayerChallengeHistory[]>([]);

  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    Promise.all([
      fetch(WEEKLY_URL).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load weekly challenges.");
        }

        return response.json();
      }),
      fetch(WEEKLY_LAST_URL).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load weekly challenges.");
        }

        return response.json();
      }),
      fetch(STANDINGS_URL).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load standings.");
        }

        return response.json();
      }),
      fetch(HISTORY_URL).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load challenge history.");
        }

        return response.json();
      }),
    ])
      .then(([weeklyData, lastWeeklyData, standingsData, historyData]) => {
        if (!alive) {
          return;
        }

        setWeeklyChallenges(weeklyData);
        setLastWeeklyChallenges(lastWeeklyData);
        setStandings(standingsData);
        setChallengeHistory(historyData);
      })
      .catch(() => {
        if (!alive) {
          return;
        }

        setWeeklyChallenges([]);
        setLastWeeklyChallenges([]);
        setStandings([]);
        setChallengeHistory([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  const historyByPlayer = useMemo(() => {
    return new Map(
      challengeHistory.map((player) => [
        player.playerName.trim().toLocaleLowerCase(),
        player.challenges,
      ])
    );
  }, [challengeHistory]);

  const togglePlayer = (playerName: string) => {
    setExpandedPlayer((current) =>
      current === playerName ? null : playerName
    );
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(`${date}T00:00:00`));
  };

  return (
    <div className="surface-body">
      <h1>{t("nyeremenyjatek.title")}</h1>

      <section>
        <p>{t("nyeremenyjatek.intro1")}</p>
        <p>{t("nyeremenyjatek.intro2")}</p>
        <p>{t("nyeremenyjatek.intro3")}</p>
      </section>

      <section className="weekly-challenges-section">

        <h2 className="mc-title">Heti Challenge-ek</h2>
          
        {challengeNotices.length > 0 && (
          <div className="challenge-notices">
            {challengeNotices.map((notice, index) => (
              <div className="challenge-notice" key={index}>
                <span className="challenge-notice-icon">ℹ</span>
                <span>{notice}</span>
              </div>
            ))}
          </div>
        )}

        <div className="challenges-grid">
          {weeklyChallenges.map((challenge, index) => (
            <XChallengeCard
              key={`${challenge.title}-${challenge.weekstart}-${index}`}
              title={challenge.title}
              short={challenge.short}
              weekstart={challenge.weekstart}
              weekend={challenge.weekend}
              reward={challenge.reward}
            />
          ))}
        </div>
      </section>

      
      {lastWeeklyChallenges.length > 0 && (
        <section className="weekly-challenges-section">
          <h2 className="mc-title">Múlt heti Challenge-ek</h2>

          <div className="challenges-grid">
            {lastWeeklyChallenges.map((challenge, index) => (
              <XChallengeCard
                key={`${challenge.title}-${challenge.weekstart}-${index}`}
                title={challenge.title}
                short={challenge.short}
                weekstart={challenge.weekstart}
                weekend={challenge.weekend}
                reward={challenge.reward}
              />
            ))}
          </div>
        </section>
      )}

      <section style={{ marginTop: "1.5rem" }}>
        <h2>{t("nyeremenyjatek.standingsTitle")}</h2>

        <div className="table-wrap">
          <table className="nice-table standings-table">
            <colgroup>
              <col className="col-player" />
              <col className="col-points" />
              <col className="col-earned" />
              <col className="col-mobile-summary" />
            </colgroup>
            <thead>
              <tr>
                <th>{t("nyeremenyjatek.table.trainer")}</th>
                <th>
                  {t("nyeremenyjatek.table.total")}
                </th>
                <th>
                  {t("nyeremenyjatek.table.codeCardsEarned")}
                </th>
              </tr>
            </thead>

            <tbody>
              {standings.map((standing) => {
                const isExpanded = expandedPlayer === standing.name;

                const playerChallenges =
                  historyByPlayer.get(
                    standing.name.trim().toLocaleLowerCase()
                  ) ?? [];

                return (
                  <Fragment key={standing.name}>
                    <tr
                        className="standing-row"
                        onClick={() => togglePlayer(standing.name)}
                        aria-expanded={isExpanded}
                      >
                        <td>
                          <button
                            type="button"
                            className="player-expand-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePlayer(standing.name);
                            }}
                          >
                            <span className="expand-icon">
                              {isExpanded ? "▾" : "▸"}
                            </span>

                            <span className="player-name">
                              {standing.name}
                            </span>
                          </button>
                        </td>

                        <td>
                          {standing.points}
                          </td>
                        <td>
                          {standing.codeCardsEarned - standing.codeCardsGivenOut}
                          </td>
                      </tr>

                    {isExpanded && (
                      <tr className="challenge-history-row">
                        <td colSpan={4}>
                          <div className="challenge-history">
                            {playerChallenges.length === 0 ? (
                              <p className="challenge-history-empty">
                                Nincsenek teljesített challenge-ek.
                              </p>
                            ) : (
                              <div className="challenge-history-list">
                                {playerChallenges.map((challenge, index) => (
                                  <div
                                    className="challenge-history-item"
                                    key={`${challenge.title}-${challenge.weekEnd}-${index}`}
                                  >
                                    <div className="challenge-history-title">
                                      {challenge.title}
                                    </div>

                                    <div className="challenge-history-date">
                                      <span className="challenge-history-label">
                                        Hét vége
                                      </span>

                                      {formatDate(challenge.weekEnd)}
                                    </div>

                                    <div className="challenge-history-reward">
                                      <span className="challenge-history-label">
                                        Jutalom
                                      </span>

                                      {challenge.reward}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}